import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  Sun, User, Euro,
  Moon, 
  Trash2,
  Settings as SettingsIcon,
  CalendarDays,
  Clock,
  Plus,
  Minus,
  Calendar,
  LogOut,
  SquareUserRound,
  Shield,
  Book,
  Key,
  Info,
  GraduationCap,
  FileText,
  ChevronRight
} from 'lucide-react';
import { useSupabase } from '../context/SupabaseContext';
import { useAuth } from '../context/AuthContext';
import { baseTeacher } from '../components/interfaces/All';


const Settings: React.FC = () => {

  const { signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showClearModal, setShowClearModal] = useState(false);


  const { state, fetchUserRole, updateTeacherData, fetchTeachers, savePairingCode, updateStudentData } = useSupabase();

  const [pricePerH, setPricePerH] = useState(0);

  const [teacherSettings, setTeacherSettings] = useState(baseTeacher.teacher_settings);
  const userRole = state.userRole;

  useMemo(() => {
      fetchUserRole();
  }, []);


  // teacher settings
  useEffect(() => {
    if(state.userRole !== 'teacher') return
    handleSetTeacherSettings(state.userProfile.teacher_settings || baseTeacher.teacher_settings );
  }, [state.userProfile.id]);

  const handleSetTeacherSettings = (setttings:any)=>{
    if (state.userRole !== 'teacher' || !state.userProfile.id) return;
    setTeacherSettings(setttings)
    const timeout = setTimeout(async () => {
      await updateTeacherData( {
        id: state.userProfile.id!,
        teacher_settings: teacherSettings
      });
    }, 800); // Debounce 800ms

    return () => clearTimeout(timeout);
  }


  // price per H change
  useEffect(() => {
    if(state.userRole !== 'teacher') return
    if(!state.userProfile.price_per_h) {
      console.error("Price per hour not set in user profile");
      return;
    };
    // Load from state.userProfile (already synced)
    setPricePerH(state.userProfile.price_per_h);
  }, [state.userProfile.price_per_h]);

  // One-liner change handler - no debouncing needed!
  const handlePriceChange = (value: number) => {
    if(state.userRole !== 'teacher') return
    setPricePerH(value);
    updateTeacherData({
      id: state.userProfile.id!, price_per_h: value 
    });
  };

  const updateSessionLength = (minutes: number) => {
    setTeacherSettings(prev => ({
      ...prev,
      sessionLengthMinutes: Math.max(15, Math.min(240, minutes)) // 15min-4hr limits
    }));
  };
  // Toggle working day
  const toggleWorkingDay = (day: string) => {
    setTeacherSettings(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day]
    }));
  };
  const updateWorkingHours = (day: string, field: 'start' | 'end', time: string) => {
    setTeacherSettings(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: { ...prev.workingHours[day], [field]: time }
      }
    }));
  };

  const handleLogout = async () => {
    try {
      signOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Add new break (default lunch)
  const addNewBreak = () => {
    const newBreak = {
      id: Date.now().toString(),
      start: '12:00',
      end: '13:00'
    };

    setTeacherSettings(prev => ({
      ...prev,  // Keep sessionLengthMinutes, workingDays, workingHours
      breaks: [...prev.breaks, newBreak]  // ONLY update breaks array
    }));
  };

  // Update specific break
  const updateBreak = (breakId: string, field: 'start' | 'end', time: string) => {
    setTeacherSettings(prev => ({
      ...prev,
      breaks: prev.breaks.map(b =>
        b.id === breakId
          ? { ...b, [field]: time }
          : b
      )
    }));
  };

  // Remove break
  const removeBreak = (breakId: string) => {
    setTeacherSettings(prev => ({
      ...prev,
      breaks: prev.breaks.filter(b => b.id !== breakId)
    }));
  };

  const handleSetTeacher = (teacherId: string) => {
    updateStudentData(
      { id: state.userProfile.id!, teacher_id: [state.userProfile.teacher_id, teacherId] }
    );
  };

  // Day names mapping
  const DAYS = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  // Clear all routes from localStorage
  const handleClearAll = () => {
    localStorage.removeItem('gpxRoutes');
    localStorage.removeItem('trackers');
    localStorage.removeItem('pendingSync');
    window.location.href = '/dashboard';
  };

  // const saveTeacherSettings = async () => {
  //   try {
  //   console.log("saving teacher settings")
  //   updateTeacherData(state.userProfile.id!, { teacher_settings: teacherSettings });
  //   } catch (error) {
  //     console.error('Save failed:', error);
  //   }
  // };

  return (
    <div className="max-w-2xl mx-auto fade-in">
      <div className="mb-6">

      </div>

      {/* User role dropdown*/}
      <div className="card mb-6 flex ">
        <div className="p-6 border-b border-border items-start gap-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-grid">
              <User size={30} className=" col-start-1 row-start-1"/>
              {state.userRole === 'teacher' && (
                <span className="col-start-1 row-start-1 self-end justify-self-end bg-white rounded-full dark:bg-gray-800">
                  <Book
                  size={15}
                  className="col-start-1 row-start-1 self-end justify-self-end z-10 translate-y-1"
                />
                </span>
              )}
              {state.userRole === 'student' && (
                <span className="col-start-1 row-start-1 self-end justify-self-end bg-white rounded-full dark:bg-gray-800">
                  <GraduationCap
                    size={14}
                    className="col-start-1 row-start-1 self-end justify-self-end z-10 translate-y-1"
                  />
                </span>
              )}
              {state.userRole === 'parent' && (
                <span className="col-start-1 row-start-1 self-end justify-self-end bg-white rounded-full dark:bg-gray-800">
                  <Shield
                  size={15}
                  className="col-start-1 row-start-1 self-end justify-self-end z-10 translate-y-1"
                />
                </span>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold">{state.userProfile?.email}</h2>
              <p className="text-text-light text-sm">
                {userRole} account
              </p>
            </div>
          </div>
        </div>
      </div>


      {/* Theme Settings */}
      <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Appearance</h2>
          <p className="text-text-light text-sm">Customize the app appearance</p>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Moon size={20} className="text-text-light" />
              ) : (
                <Sun size={20} className="text-text-light" />
              )}
              <div>
                <p className="font-medium">Theme</p>
                <p className="text-sm text-text-light">
                  Current: {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                </p>
              </div>
            </div>

            <button
              onClick={toggleTheme}
              className="btn btn-outline"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
        </div>
      </div>


      {/* Local Database */}
      {/* {state.userRole === 'teacher'? <>
      <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Pair Local Database</h2>
          <p className="text-text-light text-sm">Connect your local database to the main API</p>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
             Indicator of your database
            </div>

          </div>
        </div>
      </div>
      </>:<></>} */}

      {/* Selected Teacher */}
      {state.userRole === 'student'? <>
      <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">My Teacher</h2>
          {/* <p className="text-text-light text-sm">Your teacher</p> */}
        </div>

        <div className="p-6">
          <ul className="list-disc pt-3 pb-4">
            {state.teachers.map((t)=>(
              <li key={t.id} className="flex items-left gap-3">
                <img className="rounded-xl" width={20} height={20} alt="teacherprofile picture" src={t.picture} />
                <div>
                  {t.first_name} {t.last_name} - {t.email}
                </div>
              </li>
            ))} 
          </ul>

          <div className="text-sm text-text-light flex items-start gap-2 p-1">
            <Info size={25}/>
            <p>
            To change teacher, please contact them to remove you from their list.
            </p>
          </div>
        </div>
      </div>
      </>:<></>}

      {/* Teacher settings pannel */}
      <div className="card mb-6">
        {state.userRole === 'teacher'? <>
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <CalendarDays size={20} />
                <h2 className="text-lg font-semibold">Schedule Settings</h2>
              </div>
              <p className="text-text-light text-sm mt-1">
                Configure your working hours and breaks for automatic timeslot generation
              </p>
            </div>

            <div className=" p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-emerald-800">
                📏 Session Length
              </h3>
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium w-32">Default Duration</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={teacherSettings.sessionLengthMinutes}
                    onChange={(e) => updateSessionLength(parseInt(e.target.value))}
                    min="15"
                    max="240"
                    step="15"
                    className="input-field w-24 text-center"
                    placeholder="60"
                  />
                  <span className="text-sm font-medium text-text-light">min</span>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Working Days */}
              <div>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Calendar size={18} />
                  Working Days
                </h3>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                  {DAYS.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => toggleWorkingDay(key)}
                      className={`p-1 rounded-lg text-sm font-medium transition-all h-12 flex flex-col items-center gap-1 ${
                        (teacherSettings.workingDays || []).includes(key)
                          ? 'bg-green-500 text-white shadow-md'
                          : 'bg-gray-100 hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      <span className="font-bold">{label}</span>
                      <span className="text-xs opacity-75">
                        {(teacherSettings.workingDays || []).includes(key) ? 'ON' : 'OFF'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Working Hours */}
              <div>
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Clock size={18} />
                  Working Hours
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {DAYS.map(({ key }) => (
                    (teacherSettings.workingDays || []).includes(key) && (
                      <div key={key} className="space-y-2">
                        <label className="text-sm font-medium block">{key.charAt(0).toUpperCase() + key.slice(1)}</label>
                        <div className="flex gap-2">
                          <input
                            type="time"
                            value={teacherSettings.workingHours[key as any]?.start || '09:00'}
                            onChange={(e) => updateWorkingHours(key as any, 'start', e.target.value)}
                            className="input-field flex-1"
                          />
                          {/* <span className="px-2 py-2 text-text-light text-sm font-medium"></span> */}
                          <input
                            type="time"
                            value={teacherSettings.workingHours[key as any]?.end || '18:00'}
                            onChange={(e) => updateWorkingHours(key as any, 'end', e.target.value)}
                            className="input-field flex-1"
                          />
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>

              {/* Breaks Section - With Add/Remove */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Minus size={18} />
                    Daily Breaks
                  </h3>

                  {/* ✅ SECOND ADD BUTTON */}
                  <button
                    onClick={addNewBreak}
                    className="btn btn-outline flex items-center gap-2 text-xs"
                    title="Add another break (12:00-13:00 default)"
                  >
                    <Plus size={14} />
                    Add Break
                  </button>
                </div>

                <div className="space-y-3">
                  {teacherSettings.breaks.map(breakItem => (
                    <div key={breakItem.id} className="flex items-center gap-4  group">
                      {/* Time inputs */}
                      <div className="flex items-center gap-1 flex-1">
                        <input
                          type="time"
                          value={breakItem.start}
                          onChange={(e) => updateBreak(breakItem.id, 'start', e.target.value)}
                          className="input-field"
                        />
                        {/* <span className="px-3 py-2 text-text-light font-medium text-sm">to</span> */}
                        <input
                          type="time"
                          value={breakItem.end}
                          onChange={(e) => updateBreak(breakItem.id, 'end', e.target.value)}
                          className="input-field"
                        />
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() => removeBreak(breakItem.id)}
                        className="p-2 text-red-500 hover:bg-red-100 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                        title="Remove this break"
                      >
                        <Minus size={16} />
                      </button>
                    </div>
                  ))}

                  {/* Empty state with first add button */}
                  {teacherSettings.breaks.length === 0 && (
                    <div className="p-8 border-2 border-dashed border-gray-300 rounded-2xl text-center hover:border-orange-400 transition-colors cursor-pointer group">
                      <button
                        onClick={addNewBreak}
                        className="flex flex-col items-center gap-2 p-6 group-hover:text-orange-600 transition-colors w-full h-full"
                      >
                        <Plus size={24} className="opacity-50 group-hover:opacity-100" />
                        <div>
                          <p className="font-medium">Add First Break</p>
                          <p className="text-xs text-text-light mt-1">Lunch break (12:00-13:00)</p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                <p className="text-xs text-text-light mt-3 ml-1 opacity-75">
                  All breaks apply to working days ({teacherSettings.workingDays.length} days)
                </p>
              </div>
            </div>
        </>:<></>}
      </div>

      {/* TEACHERS SIGNUP FORMS */}
      {state.userRole === 'teacher' && (
        <div className="card mb-6"
          onClick={() => navigate('/forms')}
        >
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <FileText size={20} />
              <h2 className="text-lg font-semibold">Signup Forms</h2>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-text-light text-sm mt-1">
                Create public signup forms for your students. Share the link with them to sign up for lessons.
              </p>
              <ChevronRight size={20} />
            </div>
          </div>
        </div>
      )}


      {/* TEACHERS PRICE PER HOUR */}
      {state.userRole === 'teacher' && (
        <div className="card mb-6">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <CalendarDays size={20} />
              <h2 className="text-lg font-semibold">Price per hour</h2>
            </div>
            <p className="text-text-light text-sm mt-1">
              Configure your price per hour. This applies immediately to all upcomming lessons. Concluded
            </p>
          </div>

          <div className="p-6">
            <label className="block text-sm font-medium mb-1">
              Price per 60 minutes
            </label>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-text-light" size={18} />
                <input
                  type="number"
                  value={pricePerH}
                  onChange={(e) => handlePriceChange(Number(e.target.value))}
                  className="input-field pl-10"
                />
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Sync Status */}
      {/* <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Sync Status</h2>
          <p className="text-text-light text-sm">Data synchronization with cloud services</p>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isOnline ? (
                <Wifi size={20} className="text-green-500" />
              ) : (
                <WifiOff size={20} className="text-red-500" />
              )}
              <div>
                <p className="font-medium">
                  {isOnline ? 'Online' : 'Offline'}
                </p>
                <p className="text-sm text-text-light">
                  Last sync: {formatLastSync(syncStatus.lastSync)}
                </p>
              </div>
            </div>

            <button
              onClick={performSync}
              disabled={!isOnline || isSyncing}
              className="btn btn-outline disabled:opacity-50"
            >
              <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>

          {syncStatus.pending > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {syncStatus.pending} items pending sync
              </p>
            </div>
          )}

          {syncStatus.failed > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">
                {syncStatus.failed} items failed to sync
              </p>
            </div>
          )}
        </div>
      </div> */}

      {/* Trackers Section */}
      {/* <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">GPS Trackers</h2>
              <p className="text-text-light text-sm">Manage your connected tracking devices</p>
            </div>
            <button
              onClick={() => setShowAddTrackerModal(true)}
              className="btn btn-primary"
            >
              <Plus size={18} className="mr-2" />
              Add Tracker
            </button>
          </div>
        </div>

        <div className="divide-y divide-border">
          {trackers.map(tracker => (
            <div key={tracker.id} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Signal size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">{tracker.label}</h3>
                    <p className="text-sm text-text-light">Code: {tracker.code}</p>
                    <p className="text-xs text-text-light">
                      Last sync: {new Date(tracker.lastSync).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteTracker(tracker.id)}
                  className="p-2 text-text-light hover:text-red-500 transition-colors"
                  aria-label="Delete tracker"
                >
                  <Trash size={18} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Update Interval (seconds)
                  </label>
                  <input
                    type="number"
                    value={tracker.settings.updateInterval}
                    onChange={(e) => handleUpdateTrackerSettings(tracker.id, {
                      updateInterval: parseInt(e.target.value)
                    })}
                    className="input-field"
                    min="30"
                    max="3600"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Geofence Radius (meters)
                  </label>
                  <input
                    type="number"
                    value={tracker.settings.geofenceRadius}
                    onChange={(e) => handleUpdateTrackerSettings(tracker.id, {
                      geofenceRadius: parseInt(e.target.value)
                    })}
                    className="input-field"
                    min="50"
                    max="1000"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium">Power Save Mode</label>
                    <p className="text-xs text-text-light">
                      Extends battery life by reducing update frequency
                    </p>
                  </div>
                  <button
                    onClick={() => handleUpdateTrackerSettings(tracker.id, {
                      powerSaveMode: !tracker.settings.powerSaveMode
                    })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      tracker.settings.powerSaveMode ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        tracker.settings.powerSaveMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {trackers.length === 0 && (
            <div className="p-6 text-center text-text-light">
              <MapPin size={24} className="mx-auto mb-2" />
              <p>No trackers added yet</p>
            </div>
          )}
        </div>
      </div> */}

      {/* Data Settings */}
      {/* <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Data Management</h2>
          <p className="text-text-light text-sm">Manage your route data</p>
        </div>

        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database size={20} className="text-text-light" />
              <div>
                <p className="font-medium">Storage Usage</p>
                <p className="text-sm text-text-light">
                  {getStorageUsage()} • {routes.length} routes • {trackers.length} trackers
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trash2 size={20} className="text-red-500" />
              <div>
                <p className="font-medium">Clear All Data</p>
                <p className="text-sm text-text-light">
                  Delete all saved routes, trackers, and reset app
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowClearModal(true)}
              className="btn bg-red-500 text-white hover:bg-red-600"
              disabled={routes.length === 0 && trackers.length === 0}
              aria-label="Clear all data"
            >
              Clear Data
            </button>
          </div>
        </div>
      </div> */}

      {/* About */}
      <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">About</h2>
          <p className="text-text-light text-sm">Application information</p>
        </div>

        <div className="p-6 flex items-start gap-4">
          <div className="bg-primary text-white p-2 rounded-lg mt-1">
            <img src="/icons/logo.svg" className="dark:invert" width={100} alt="Logo" />
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-1">Kite</h3>
            <p className="text-text-light mb-4">
              A client dasboard application with scheduling and tracking features, designed for teachers and students. Built with React, Tailwind CSS, and Supabase for seamless data management and real-time updates.
            </p>

            <div className="text-sm text-text-light">
              <p>Version 0.0.1</p>
              <p>Copyright 2026</p>
            </div>
          </div>
        </div>
      </div>



      {/* Clear data confirmation modal */}
      {showClearModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 p-4">
          <div className="card max-w-md w-full p-6 fade-in slide-up">
            <div className="text-red-500 mb-4">
              <AlertIcon size={32} />
            </div>

            <h3 className="text-xl font-bold mb-2">Clear All Data?</h3>

            <p className="text-text-light mb-6">
              This will permanently delete all your routes, trackers, and reset the app.
              This action cannot be undone.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                className="btn text-text-light hover:bg-background"
                onClick={() => setShowClearModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn bg-red-500 text-white hover:bg-red-600"
                onClick={handleClearAll}
              >
                Yes, Clear All Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log out */}

      <div className="card mb-6">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Admin</h2>
        </div>

        <div className="p-6">
          <button
            onClick={handleLogout}
            className="btn btn-outline-danger flex items-center w-full text-left"
            aria-label="Sign out"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </div>






    </div>


  );
};

// Alert Icon Component
const AlertIcon: React.FC<{ size: number }> = ({ size }) => {
  return (
    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
      <Trash2 size={size * 0.5} className="text-red-500" />
    </div>
  );
};

export default Settings;
